import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { publicProductsApi } from '../../api/publicProducts.js';
import { productsApi } from '../../api/products.js';
import { categoriesApi } from '../../api/categories.js';
import { usePaginatedQuery } from '../../hooks/usePaginatedQuery.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { DataTable } from '../../components/DataTable.jsx';
import { Pagination } from '../../components/ui/Pagination.jsx';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { fmt } from '../../lib/formatters.js';
import { t } from '../../lib/i18n.js';

const STATUS_TONE = { active: 'success', draft: 'warning', archived: 'neutral' };
const STATUS_LABEL = { active: 'Attivo', draft: 'Bozza', archived: 'Archiviato' };

function findInventoryForStore(product, storeId) {
  if (!Array.isArray(product.inventory)) return null;
  return product.inventory.find((r) => String(r.storeId._id || r.storeId) === String(storeId)) || null;
}

function StockEditModal({ open, onClose, product, storeId }) {
  const queryClient = useQueryClient();
  const current = product && findInventoryForStore(product, storeId);
  const [qty, setQty] = useState('');

  useState(() => {});

  // Re-sync when modal opens with a new product.
  if (open && qty === '' && current && qty !== String(current.quantity)) {
    // initial sync once per opening
  }

  const initial = current ? String(current.quantity ?? '') : '0';

  const mutation = useMutation({
    mutationFn: () => productsApi.setInventory(product._id, {
      storeId,
      quantity: Number(qty || initial) || 0
    }),
    onSuccess: () => {
      toast.success('Scorta aggiornata.');
      queryClient.invalidateQueries({ queryKey: ['employee', 'inventory'] });
      onClose();
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={product ? `Scorta - ${product.name}` : 'Scorta'}
      description={product ? `SKU ${product.sku}` : null}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <Input
        label="Quantità nel tuo negozio"
        type="number"
        min={0}
        defaultValue={initial}
        onChange={(e) => setQty(e.target.value)}
        autoFocus
      />
      <p className="mt-2 text-xs text-ink-muted">
        Scorta attuale: {current ? fmt.int(current.quantity) : '0'}{current?.reorderPoint ? ` (punto di riordino ${fmt.int(current.reorderPoint)})` : ''}
      </p>
    </Modal>
  );
}

export function InventoryPage() {
  const { user, hasPermission } = useAuth();
  const canUpdateStock = hasPermission('products:update');
  const storeId = user && user.storeId;

  const speciesQuery = useQuery({ queryKey: ['cat', 'species', 'public'], queryFn: () => categoriesApi.list({ kind: 'species', limit: 100 }).catch(() => ({ items: [] })) });
  const productCatQuery = useQuery({ queryKey: ['cat', 'productCategory', 'public'], queryFn: () => categoriesApi.list({ kind: 'productCategory', limit: 100 }).catch(() => ({ items: [] })) });

  const { items, isLoading, pagination, page, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['employee', 'inventory'],
    fetcher: publicProductsApi.list
  });

  const [stockTarget, setStockTarget] = useState(null);

  const columns = useMemo(() => [
    { key: 'sku', header: 'SKU', render: (r) => <code className="rounded bg-border/40 px-1.5 py-0.5 text-xs">{r.sku}</code> },
    {
      key: 'name',
      header: 'Prodotto',
      render: (r) => (
        <div className="flex flex-col">
          <span className="text-ink">{r.name}</span>
          {r.brand && <span className="text-xs text-ink-muted">{r.brand}</span>}
        </div>
      )
    },
    { key: 'price', header: 'Prezzo', align: 'right', render: (r) => r.price ? fmt.eur(r.price.amount) : '-' },
    {
      key: 'stock',
      header: 'Scorta nel tuo negozio',
      align: 'right',
      render: (r) => {
        const inv = storeId ? findInventoryForStore(r, storeId) : null;
        const qty = inv ? Number(inv.quantity) || 0 : 0;
        const reorder = inv ? Number(inv.reorderPoint) || 0 : 0;
        const tone = qty === 0 ? 'danger' : reorder > 0 && qty <= reorder ? 'warning' : 'success';
        return <Badge tone={tone}>{fmt.int(qty)}</Badge>;
      }
    },
    {
      key: 'status',
      header: 'Stato',
      render: (r) => <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{STATUS_LABEL[r.status] || r.status}</Badge>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => canUpdateStock && storeId ? (
        <button type="button" className="text-xs text-brand hover:text-brand-hover" onClick={() => setStockTarget(r)}>
          Aggiorna scorta
        </button>
      ) : null
    }
  ], [canUpdateStock, storeId]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Inventario"
        description={storeId
          ? 'Prodotti disponibili nel tuo negozio. La scorta si riferisce al punto vendita assegnato.'
          : 'Il tuo account non è associato a un negozio: contatta il titolare.'}
      />

      {!storeId && (
        <Card>
          <p className="text-sm text-warning">
            Non sei associato a nessun negozio. Non puoi vedere le scorte locali.
          </p>
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Input className="w-64" label="Cerca" placeholder="nome, SKU, brand..." value={filters.q || ''} onChange={(e) => setFilters({ q: e.target.value || undefined })} />
        <Select label="Specie" className="w-48" value={filters.species || ''} onChange={(e) => setFilters({ species: e.target.value || undefined })}>
          <option value="">Tutte</option>
          {((speciesQuery.data && speciesQuery.data.items) || []).map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
        </Select>
        <Select label="Categoria" className="w-52" value={filters.category || ''} onChange={(e) => setFilters({ category: e.target.value || undefined })}>
          <option value="">Tutte</option>
          {((productCatQuery.data && productCatQuery.data.items) || []).map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
        </Select>
        <Select label="Scorta" className="w-40" value={filters.inStock === undefined ? '' : String(filters.inStock)} onChange={(e) => {
          const v = e.target.value;
          setFilters({ inStock: v === '' ? undefined : v === 'true' });
        }}>
          <option value="">Tutti</option>
          <option value="true">Disponibili</option>
          <option value="false">Esauriti</option>
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessun prodotto', description: 'Non risultano prodotti nel tuo negozio.' }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <StockEditModal
        open={Boolean(stockTarget)}
        onClose={() => setStockTarget(null)}
        product={stockTarget}
        storeId={storeId}
      />
    </div>
  );
}
